import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearch } from '../api/queries'
import type { SearchHit } from '../api/types'

const GROUPS: { key: 'projects' | 'customers' | 'devices' | 'tasks' | 'users' | 'comments'; label: string; icon: string }[] = [
  { key: 'projects', label: 'Sager', icon: '📁' },
  { key: 'customers', label: 'Kunder', icon: '🏢' },
  { key: 'devices', label: 'Enheder', icon: '🔋' },
  { key: 'tasks', label: 'Opgaver', icon: '✅' },
  { key: 'users', label: 'Personer', icon: '👤' },
  { key: 'comments', label: 'Kommentarer', icon: '💬' }
]

/**
 * Den store søgning i topbjælken (beslutning 110): sager, kunder, enheder, opgaver, personer og kommentarer på én
 * gang - også på adresse og postnummer. Enter åbner det første resultat, Escape lukker.
 */
export function GlobalSearch() {
  const navigate = useNavigate()
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250)
    return () => clearTimeout(timer)
  }, [term])

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (box.current && !box.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  const { data, isFetching } = useSearch(debounced)

  const groups = GROUPS.map((g) => ({ ...g, hits: (data?.[g.key] ?? []) as SearchHit[] })).filter((g) => g.hits.length > 0)
  const first = groups.flatMap((g) => g.hits).find((hit) => hit.link)

  function go(hit: SearchHit) {
    if (!hit.link) return
    setOpen(false)
    setTerm('')
    navigate(hit.link)
  }

  return (
    <div className="global-search" ref={box}>
      <input
        type="search"
        placeholder="Søg sag, kunde, enhed, adresse ..."
        aria-label="Søg i hele systemet"
        value={term}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setTerm(e.target.value)
          setOpen(true)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false)
          if (e.key === 'Enter' && first) {
            e.preventDefault()
            go(first)
          }
        }}
      />

      {open && debounced.length >= 2 && (
        <div className="global-search__results">
          {isFetching && groups.length === 0 && <div className="tiny muted global-search__empty">Søger ...</div>}
          {!isFetching && data && groups.length === 0 && (
            <div className="tiny muted global-search__empty">Intet fundet for "{debounced}".</div>
          )}
          {groups.map((group) => (
            <div key={group.key}>
              <div className="global-search__group">
                {group.icon} {group.label}
              </div>
              {group.hits.map((hit) => (
                <button
                  key={`${hit.type}-${hit.id}`}
                  type="button"
                  className="global-search__hit"
                  disabled={!hit.link}
                  onClick={() => go(hit)}
                >
                  <span className="global-search__title">{hit.title}</span>
                  {hit.subtitle && <span className="tiny muted global-search__sub">{hit.subtitle}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
